import type {
  Action,
  Attribute,
  DataRoute,
  List,
  Outcome,
  PublishedAttribute,
  ReasonCodeGroup,
  ServiceObject,
  Tag,
  Threshold,
  WorkflowFailureHandlingMode,
  WorkflowType,
} from 'flow/store/engine/types';
import type { CustomList } from 'flow/service/actions/services/customLists';
import type { ServiceConfigOption } from '~/dashboard/views/settings/service-configuration/types';
import type { APIClient } from '../../apiClient';
import { workflowRoutes } from './workflowRoutes';
import type { SdkConfigPayload, SdkConfigSavePayload } from
  'flow/editor/components/MenuBar/MenuBarModals/ThreeDotsModals/SDKConfigModal/types';

export type WorkflowSchema = {
  name: string;
  token: string;
  workflowType: WorkflowType;
  actions: Record<string, Action>;
  thresholds: Record<string, Threshold>;
  dataRoutes: Record<string, DataRoute>;
  failureHandlingMode: WorkflowFailureHandlingMode;
  // Save path builds SdkConfigSavePayload via buildSdkConfigForSave — base `fields` plus a nested
  // per-language `translations` map. Read paths (e.g. snippet details) pass a raw SdkConfigPayload.
  sdkConfig: SdkConfigSavePayload | SdkConfigPayload;
};

export type SchemaFieldKind = 'primitive' | 'object' | 'array' | 'unknown';

export type SchemaField = {
  name: string;
  kind: SchemaFieldKind;
};

export type SchemaFieldsResponse = {
  fields: SchemaField[];
};

export type CreateWorkflowVersionPayload = {
  workflowVersion: WorkflowSchema;
  workflowVersionMetadata: {
    changes: string;
    notes: string;
    parentPublicVersion: number | null;
  };
  accountArtifacts: {
    services: ServiceObject[];
    publishedAttributes?: PublishedAttribute[];
  };
  snippetVersion?: {
    props: {
      tags: Tag[];
      outcomes: Outcome[];
      services: ServiceObject[];
      publishedAttributes: PublishedAttribute[];
      reasonCodeGroups: ReasonCodeGroup[];
      lists: List[];
      customLists: CustomList[];
      globalServiceConfigOptions: ServiceConfigOption[];
      universalAttributes: Attribute[];
    };
  };
};

export default function createWorkflowsAPI(apiClient: APIClient) {
  return {
    versions: {
      // todo - add return type when API contract is finalized
      createWorkflowVersion: async (
        data: CreateWorkflowVersionPayload,
      ) => apiClient.post(
        workflowRoutes.workflowVersion(
          data.workflowVersion.token,
        ),
        data,
        {
          meta: {
            skipTransformRequestBody: true,
            skipTransformResponseBody: true,
          },
        },
      ),
      getSchemaFields: async (params: {
        workflowToken: string;
        internalName: string;
        pathPrefix?: string;
      }): Promise<SchemaFieldsResponse> => {
        const queryParams = new URLSearchParams();
        queryParams.append('internalName', params.internalName);
        if (params.pathPrefix) queryParams.append('pathPrefix', params.pathPrefix);

        const url = `${workflowRoutes.workflowVersionSchemaFields(params.workflowToken)}?${queryParams.toString()}`;

        return apiClient.get<SchemaFieldsResponse>(url, {
          meta: {
            name: 'Get Workflow Version Schema Fields',
          },
        });
      },
    },
  };
}
